// Admin UI design tokens — radius, shadow and density, white-label neutral skin.
//
// Resolves the `DesignTokens` module alias for the admin build only (same
// mechanism as Palette — see src/components/admin/Palette.ts for the full
// explanation). The Investor UI keeps its current look via
// src/components/non_admin/DesignTokens.ts, which reproduces each of these
// same keys with the exact values the components already had, so the
// Investor UI is pixel-identical to before.
//
// Admin skews toward a denser, more "business tool" feel than the Investor
// UI's rounded/spacious consumer look: smaller radii, subtler shadows,
// tighter section padding.
import { Palette } from 'Palette';

export const RADIUS = {
  propertyCard: '10px',
  propertyCardMini: '8px',
  section: '12px',
  sectionChild: '8px',
  modal: '12px',
  button: '8px',
  cardContainer: '10px',
  sectionCard: '10px',
  card: 10,
};

export const SHADOW = {
  cardContainerHover: '0 8px 20px rgba(15,23,42,0.10)',
  sectionCard: '0 1px 3px rgba(15,23,42,0.06)',
  card: '0 1px 2px rgba(15,23,42,0.08)',
};

export const SPACING = {
  sectionPaddingX: '1.25rem',
  sectionPaddingY: '1.25rem',
};

// Image containers (e.g. PropertyCard's fund/property photo) lock to this
// aspect ratio via the CSS `aspect-ratio` property, so every card in a grid
// is the same height regardless of the source photo's own dimensions —
// robust in flex/grid layouts, unlike a percentage padding-top hack.
export const IMAGE_ASPECT_RATIO = '16 / 9';

// PropertyCardImageSection also stretches the image to fill the whole card
// (flex: 1) whenever the card has no description text, overriding the
// aspect-ratio above and producing wildly different image heights within
// the same grid (a "coming soon" card with no description stretches tall;
// a card with a description stays aspect-ratio-locked). Off for admin so
// every card in a grid gets the same image height regardless of content.
export const IMAGE_FILLS_EMPTY_CARD = false;

// An image card with no title, description or link renders its image edge to
// edge and fills the card — no empty title row or inset frame around a lone photo.
export const FLUSH_IMAGE_ONLY_CARD = true;

// Admin keeps the empty title row on a card that has a description but no title,
// so image tops stay aligned across a row.
export const HIDE_EMPTY_CARD_TITLE_ROW = false;

// StyledHeading (every section title — "Fund Details", "Addresses", etc.)
// padding. Zero top padding: vertical rhythm between stacked elements
// (title -> subtitle -> content) should come from ONE side only (the
// bottom margin of whatever comes before), never from both the previous
// element's bottom AND the next element's top adding together — that's
// what produced visibly inconsistent gaps (e.g. title-to-subtitle ending up
// bigger than subtitle-to-content purely because one had top padding and
// the other didn't).
export const HEADING_SPACING = {
  paddingTop: '0',
  // +10px above the primary page title (e.g. "EOI Open"), which uses
  // fullTopSpacing.
  paddingTopFull: '0.625rem',
  paddingBottom: '0.375rem',
  paddingBottomReduced: '0.25rem',
};

// HEADING_VARIANT_STYLE.standard.fontSize override — "EOI Open" etc. at
// 2.25rem (36px) reads oversized for admin; investor keeps the original.
export const HEADING_FONT_SIZE_STANDARD = '1.5rem';

// PropertyCardGroup's description line (e.g. "Funds currently accepting EOI
// and Applications") — Investor UI zeroes this out entirely, which reads as
// a bug once other spacing is tightened: the description now runs straight
// into the card grid below with no gap at all.
// +10px below the secondary header (e.g. "Funds currently accepting...").
export const DESCRIPTION_BOTTOM_PADDING = '1.125rem';

// Same gap carried on a PropertyCardGroup title when the section has no description.
export const TITLE_BOTTOM_PADDING: string | undefined = '1.125rem';

// -5px above the in-card title (e.g. "Blackstone Senior Debt Investment
// Opportunity"), was 16px (pt: 2).
export const CARD_HEADER_PADDING_TOP = { default: '0.6875rem', mini: '0.6875rem' };

// PropertyCardHeader's own gap below the title (before the image/body
// content starts).
export const CARD_HEADER_PADDING_BOTTOM = { default: 0.75, mini: 0.75 };

// PropertyCardFooter had no top padding at all, so a card with no body
// text (just header/image/footer) had its footer sitting flush against the
// image. PropertyCardBodyContent uses 1.5/1 (mini) for the equivalent gap —
// match it here for consistency. Zeroed as part of removing 30px total
// between in-card content and the "Find Out More" button (see
// CARD_BODY_PADDING_BOTTOM and CARD_BODY_DESCRIPTION_MARGIN_BOTTOM below —
// 16px + 12px + 2px = 30px combined).
export const CARD_FOOTER_PADDING_TOP = { default: 0, mini: 0 };

// +5px below the "Find Out More" button (was pb: 1 = 8px; 1.625 × 8 = 13px).
export const CARD_FOOTER_PADDING_BOTTOM = { default: 1.625, mini: 1.625 };

// PropertyCardBodyContent's own bottom padding — part of the 30px removed
// between in-card content and the footer (was 2/1.5 = 16px/12px).
export const CARD_BODY_PADDING_BOTTOM = { default: 0, mini: 0 };

// The description text's own bottom margin — the last few px of the 30px
// removed (was 2/1 = 16px/8px; these are MUI spacing multipliers, ×8px —
// 1.75 = 14px, 0.75 = 6px).
export const CARD_BODY_DESCRIPTION_MARGIN_BOTTOM = { default: 1.75, mini: 0.75 };

// StyledButton/StyledToggleButton/CircularTextButton/StyledFilterButton text
// size — 18px reads oversized for a business-tool button.
export const BUTTON_FONT_SIZE = '1rem';

// PropertyCardImageSection was full-bleed (0 horizontal/top inset), so the
// photo touched the card's edges directly. Insets it to roughly match the
// card's other horizontal padding (PropertyCardFooter/Header/BodyContent
// use px: 2 / 1.5 mini) so there's a visible card-color border around the
// photo, plus a smaller radius on the image itself since it's no longer
// relying on the card's own corner-clipping.
// `size`/`sizeMini` inset the image left/right (the "framed" border).
// `top`/`topMini` inset it above separately — -10px from the header-to-image
// gap (was 1rem/16px, matching `size`; now 6px).
export const IMAGE_INSET = {
  size: '1rem',
  sizeMini: '0.75rem',
  top: '0.375rem',
  topMini: '0.375rem',
};
export const IMAGE_RADIUS = '8px';

// Pins each card's footer to the bottom of the card (marginTop: auto)
// instead of letting the body-text container (flex: 1) grow to fill
// leftover space — which is what created a gap between short description
// text and the footer. Off for Investor UI, which keeps its natural
// (unequal) card heights and never needs the footer pinned.
export const PROPERTY_CARD_EQUAL_HEIGHT = true;

// PropertyCardGroup's card row: flex-wrap, same as Investor UI. (A CSS
// Grid variant was tried here to get more reliable row-stretch behavior,
// but grid's sizing algorithm interacted badly with the card itself also
// being `display: flex` — items measured at a fixed 360px height
// regardless of their own explicit height. That turned out to be a false
// alarm caused by a stale test-browser viewport size, not a real bug, but
// flex-wrap already does per-row stretch by default (each wrapped line is
// its own cross-axis alignment context), so there was no need to switch.)
export const CARD_GRID_LAYOUT = false;

// Flush the last card of each full row to the right edge (useCardRowAlignment), so full rows
// line up with a header action such as "Create Opportunity". Turned off: it absorbed a full
// row's leftover width into just the last gap, which could make that one gap look visibly
// uneven next to the others at some panel widths. Equal gaps between every card instead, same
// as Investor UI, even if a full row's last card no longer always meets the header action's edge.
export const FLUSH_FULL_CARD_ROWS = false;

// StyledCardLink ("Find Out More" inside a card) — tried restyling this as
// a button (matching "Create Opportunity"), but the direction wasn't
// wanted. Reverted to the original plain-underline-link look — same as
// Investor UI. Tokens kept (rather than removed) so the component doesn't
// need further changes if this is revisited later; every value below just
// reproduces what was already hardcoded before any of this started.
export const CARD_LINK_BUTTON = {
  background: 'transparent',
  color: undefined,
  borderRadius: 0,
  padding: '0.5rem 0',
  borderBottom: undefined,
  width: '100%',
  minWidth: undefined,
  height: undefined,
  fontFamily: 'Manrope, sans-serif',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  fontWeight: 600,
  justifyContentNoArrow: 'space-between' as const,
  textNudge: true,
  hoverBackground: undefined,
};

// Reverted along with CARD_LINK_BUTTON above — back to the original
// `flex: 1` full-width wrapper, same as Investor UI.
export const CARD_LINK_STRETCH = true;

// Extra breathing room between the fixed header and the page content below
// it (MainLayoutBase's `mt` push-under-header value) — 0 for Investor UI,
// which keeps content flush against the header.
export const CONTENT_TOP_PADDING_EXTRA = '10px';

// Applied to the MUI theme in src/index.tsx so every plain <TextField>,
// <Select> etc. in the admin app gets the same compact, consistent look
// without touching each form: 40px "small" fields (MUI's default is 56px),
// 8px corners to match the buttons, white fill so fields read the same on
// grey and white surfaces, and smaller text (both the entered value and the
// floating label) to match that more compact box size — MUI's default 1rem
// read oversized once the field itself had already been shrunk.
export const MUI_COMPONENT_OVERRIDES = {
  MuiTextField: { defaultProps: { size: 'small' as const } },
  MuiFormControl: { defaultProps: { size: 'small' as const } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 8, backgroundColor: '#FFFFFF', fontSize: '0.8125rem' },
    },
  },
  MuiInputLabel: {
    styleOverrides: { root: { fontSize: '0.8125rem' } },
  },
};

// StyledFieldRow's bottom margin (was 1rem) — form rows are tighter in admin.
export const FORM_ROW_MARGIN_BOTTOM = '0.625rem';

// GenericChatWindow (shared with the Investor UI's Vera chat) — admin gets a
// reskinned, "assistant" look; Investor UI keeps exactly what the component
// already rendered off MUI's default theme (see non_admin/DesignTokens.ts),
// so this is zero visual risk there.
export const CHAT_WINDOW = {
  // Short conversations no longer sit inside a near-empty 300px box, but long
  // ones still cap and scroll.
  messageAreaMinHeight: { xs: '40vh', md: '180px' },
  messageAreaMaxHeight: { xs: '60vh', md: '420px' },
  messageAreaRadius: '0.75rem',
  userBubbleRadius: '1rem 1rem 0.25rem 1rem',
  modelBubbleRadius: '1rem 1rem 1rem 0.25rem',
  modelBubbleBorderWidth: '1px',
  // A small avatar next to assistant messages, so the chat reads as "an AI
  // assistant" rather than a generic two-colour chat widget.
  showAssistantAvatar: true,
  avatarSize: 32,
  avatarImageSrc: '/assets/vera-avatar.jpg',
  // Drives the component's branded-vs-MUI-default colour choice below.
  useBrandedColors: true,
  // Flat admin bubbles rely on the border above for definition instead of a
  // drop shadow (matches the Recently Viewed chips' flat-card treatment).
  bubbleElevation: 0,
  timestampFontSize: '0.6875rem',
  // Attachment control lives inside the input pill (matches the header
  // search's "Type" selector pattern) instead of floating beside it.
  attachInsideInput: true,
  // Tighter vertical rhythm than MUI's default uniform 10px padding — the
  // "Hi" one-word message in particular looked bloated at the original value.
  bubblePaddingY: 0.5,
  bubblePaddingX: 1.25,
  // A circular up-arrow icon (ChatGPT-style) instead of a text "Send"
  // button, and it skips StyledButton's default 16px top margin, which was
  // pushing the button below the input's own vertical center.
  useCircularSendButton: true,
  sendButtonSize: 40,
  // Investor UI's Vera chat timestamps looked fine; this component's own
  // (model-only, half-clipped on short user bubbles) did not. Simplest
  // consistent fix for admin: drop them from both roles.
  showTimestamps: false,
  // Used only when GenericChatWindow's `unifiedComposer` prop is set.
  composerInputRadius: '12px',
  // Reverted to the original inter-bubble gap — the excess whitespace
  // around short messages was actually the markdown renderer's default
  // paragraph margins inside the bubble (see tightenMarkdownSpacing below),
  // not the gap between bubbles.
  messageGap: 1.25,
  // Zeroes MarkdownRenderer's default <p> margins inside a bubble — with
  // them, a single short line sat inside ~16-19px of browser default
  // top/bottom margin on top of the bubble's own (already small) padding.
  tightenMarkdownSpacing: true,
  // Was unset (inheriting ~1rem, the same size as the page subheading) —
  // matches the property card description text (PROPERTY_CARD_BODY_STYLE)
  // so chat content doesn't read larger than the rest of the page.
  bubbleFontSize: '0.875rem',
  bubbleLineHeight: 1.5,
};

// StyledModal's close (X) button — shared with the Investor UI's Settings
// modals. Investor UI keeps the original raw-hex bordered-square look (see
// non_admin/DesignTokens.ts); admin gets a cleaner, fully circular,
// Palette-driven button consistent with the rest of the admin icon buttons.
export const MODAL_CLOSE_BUTTON = {
  background: Palette.tone1.background,
  border: `1px solid ${Palette.default.border}`,
  borderRadius: '50%',
  boxShadow: 'none',
  color: Palette.tone1.text,
  hoverBackground: Palette.tone1.activeBg,
  hoverBorder: Palette.default.buttonBg,
};
